import React, { useState, useMemo, useEffect } from "react";
import { 
    Grid, 
    Box, 
    Typography, 
    TextField, 
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    TablePagination
} from "@mui/material";
import { useSelector } from "react-redux";
import { useGetUsersQuery } from "../../store/services/user-service";
import MainCard from "../../components/cards/MainCard";
import { styled } from '@mui/material/styles';

// Styled components for the hierarchy tree
const TreeContainer = styled(Box)(({ theme }) => ({
    height: '100%',
    overflowY: 'auto',
    overflowX: 'auto',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.body2.fontSize,
    // Ensure scrollbars are always visible when content overflows
    '&::-webkit-scrollbar': {
        width: '8px',
        height: '8px'
    },
    '&::-webkit-scrollbar-track': {
        background: theme.palette.action.hover
    },
    '&::-webkit-scrollbar-thumb': {
        background: theme.palette.action.disabled,
        borderRadius: '4px'
    }
}));

const TreeNode = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    minHeight: '32px',
    whiteSpace: 'nowrap',
    '&:hover': {
        backgroundColor: theme.palette.action.hover
    }
}));

const TreeLine = styled('span')(({ theme }) => ({
    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
    fontFamily: 'Consolas, Monaco, monospace', // Keep monospace for tree lines only
    userSelect: 'none',
    fontSize: '16px',
    lineHeight: '1',
    whiteSpace: 'pre'
}));

const TreeArrow = styled('span')(({ theme }) => ({
    cursor: 'pointer',
    marginRight: '6px',
    fontFamily: theme.typography.fontFamily,
    userSelect: 'none',
    fontSize: theme.typography.body2.fontSize,
    minWidth: '16px',
    display: 'inline-block',
    textAlign: 'center',
    color: theme.palette.text.secondary
}));

const LineManagerDefault = () => {
    const { tenancyId } = useSelector((state) => state.tenancy);
    
    const [expanded, setExpanded] = useState(new Set());
    const [initialExpanded, setInitialExpanded] = useState(false);
    const [focusedNodeId, setFocusedNodeId] = useState(null);
    
    // Pagination state for the table
    const [paginationModel, setPaginationModel] = useState({
        pageSize: 100,
        page: 0
    });

    // The tree needs every user, so it asks for the summary rows. Full rows went over the API's 6 MB
    // response limit on large tenancies, and the old 5,000 cap left bigger tenancies' trees incomplete (BD-1281).
    const treeQueryParams = useMemo(() => ({
        perPage: 50000,
        page: 0,
        includes: "",
        query: "",
        searchTerm: "",
        tenancyId: tenancyId,
        adminFilter: false,
        extendedResults: true,
        summary: true
    }), [tenancyId]);
    
    // Query params for paginated table data
    const tableQueryParams = useMemo(() => ({
        perPage: paginationModel.pageSize,
        page: paginationModel.page,
        includes: "",
        query: "",
        searchTerm: "",
        tenancyId: tenancyId,
        adminFilter: false,
        extendedResults: true
    }), [tenancyId, paginationModel]);

    // Fetch data for tree hierarchy (unpaginated)
    const {
        data: treeData,
        error: treeError,
        isFetching: treeLoading
    } = useGetUsersQuery(treeQueryParams);
    
    // Fetch data for table (paginated)
    const {
        data: tableData,
        error: tableError,
        isFetching: tableLoading
    } = useGetUsersQuery(tableQueryParams);

    // Process tree data to create hierarchical structure
    const { rootNodes, visibleNodes, focusedNodes } = useMemo(() => {
        if (!treeData?.data) return { rootNodes: [], visibleNodes: [] };

        const allUsers = treeData.data;
        const nodeMap = new Map();

        // Create nodes for all users
        allUsers.forEach(user => {
            const node = {
                id: user.id,
                name: user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
                email: user.email,
                department: user.department || '',
                lineManagerId: user.lineManagerId,
                enabled: user.enabled,
                children: []
            };
            nodeMap.set(user.id, node);
        });

        // Build complete hierarchy
        allUsers.forEach(user => {
            if (user.lineManagerId && user.id !== user.lineManagerId) {
                const parentNode = nodeMap.get(user.lineManagerId);
                const childNode = nodeMap.get(user.id);
                
                if (parentNode && childNode) {
                    // Check for circular reference
                    let current = parentNode;
                    let hasCircular = false;
                    const visited = new Set([user.id]);
                    
                    while (current && current.lineManagerId) {
                        if (visited.has(current.lineManagerId)) {
                            console.warn(`Circular reference detected involving ${user.name}`);
                            hasCircular = true;
                            break;
                        }
                        visited.add(current.id);
                        current = nodeMap.get(current.lineManagerId);
                    }
                    
                    if (!hasCircular) {
                        parentNode.children.push(childNode);
                    }
                }
            }
        });

        // Find root nodes (users who don't have a line manager or whose line manager isn't in the system)
        const rootNodes = [];
        nodeMap.forEach((node, nodeId) => {
            const hasParentInSystem = node.lineManagerId && nodeMap.has(node.lineManagerId) && node.lineManagerId !== nodeId;
            if (!hasParentInSystem && node.children.length > 0) {
                rootNodes.push(node);
            }
        });
        

        // Sort all nodes recursively
        const sortNodesRecursively = (nodes) => {
            nodes.sort((a, b) => a.name.localeCompare(b.name));
            nodes.forEach(node => {
                if (node.children.length > 0) {
                    sortNodesRecursively(node.children);
                }
            });
        };
        
        sortNodesRecursively(rootNodes);

        // Build list of visible nodes based on expanded state
        const visibleNodes = [];
        const nodeIndexMap = new Map(); // Track unique index for each node
        let nodeIndex = 0;
        
        const collectVisibleNodes = (nodes, level = 0, parentLines = []) => {
            nodes.forEach((node, index) => {
                const isLast = index === nodes.length - 1;
                const uniqueNodeKey = `${node.id}_${nodeIndex++}`;
                
                visibleNodes.push({
                    ...node,
                    uniqueKey: uniqueNodeKey, // Add unique key for React
                    level,
                    isLast,
                    hasChildren: node.children.length > 0,
                    directReportsCount: node.children.length,
                    parentLines: [...parentLines]
                });
                
                if (expanded.has(node.id) && node.children.length > 0) {
                    const newParentLines = level === 0 ? [] : [...parentLines, !isLast];
                    collectVisibleNodes(node.children, level + 1, newParentLines);
                }
            });
        };

        // No filtering needed since search is removed
        visibleNodes.length = 0; // Clear array before collecting
        nodeIndex = 0; // Reset index
        collectVisibleNodes(rootNodes);
        
        // Collect focused nodes and their descendants (only expanded ones)
        const focusedNodes = [];
        if (focusedNodeId) {
            const collectFocusedSubtree = (nodes) => {
                nodes.forEach(node => {
                    if (node.id === focusedNodeId) {
                        // Found the focused node - collect it and only expanded descendants
                        const collectExpandedDescendants = (n, level = 0) => {
                            focusedNodes.push({
                                ...n,
                                uniqueKey: `focused_${n.id}_${focusedNodes.length}`,
                                level,
                                directReportsCount: n.children.length,
                                hasChildren: n.children.length > 0
                            });
                            // Only traverse children if this node is expanded
                            if (expanded.has(n.id) && n.children.length > 0) {
                                n.children.forEach(child => collectExpandedDescendants(child, level + 1));
                            }
                        };
                        collectExpandedDescendants(node);
                    } else if (node.children.length > 0) {
                        collectFocusedSubtree(node.children);
                    }
                });
            };
            collectFocusedSubtree(rootNodes);
        }

        return { 
            rootNodes: rootNodes, 
            visibleNodes,
            focusedNodes 
        };
    }, [treeData, expanded, focusedNodeId]);
    
    // Process table data for display
    const tableUsers = useMemo(() => {
        if (!tableData?.data) return [];
        return tableData.data;
    }, [tableData]);
    
    // Handle pagination changes
    const handlePageChange = (event, newPage) => {
        setPaginationModel(prev => ({ ...prev, page: newPage }));
    };
    
    const handleRowsPerPageChange = (event) => {
        setPaginationModel({
            pageSize: parseInt(event.target.value, 10),
            page: 0
        });
    };

    const handleToggle = (nodeId, nodeLevel) => {
        setExpanded(prev => {
            const newSet = new Set(prev);
            
            // If expanding a root-level node (level 0)
            if (!prev.has(nodeId) && nodeLevel === 0) {
                // Find and remove any other expanded root nodes
                rootNodes.forEach(rootNode => {
                    if (rootNode.id !== nodeId && newSet.has(rootNode.id)) {
                        newSet.delete(rootNode.id);
                        // Also clear focus if the closed node was focused
                        if (focusedNodeId === rootNode.id) {
                            setFocusedNodeId(null);
                        }
                    }
                });
            }
            
            if (newSet.has(nodeId)) {
                newSet.delete(nodeId);
            } else {
                newSet.add(nodeId);
            }
            return newSet;
        });
    };

    // Expand first node on initial load
    useEffect(() => {
        if (!initialExpanded && rootNodes.length > 0) {
            const initialSet = new Set();
            // Only expand the first root node
            const firstRootWithChildren = rootNodes.find(node => node.children.length > 0);
            if (firstRootWithChildren) {
                initialSet.add(firstRootWithChildren.id);
            }
            setExpanded(initialSet);
            setInitialExpanded(true);
        }
    }, [rootNodes, initialExpanded]);

    const renderTreeNode = (node) => {
        const isExpanded = expanded.has(node.id);
        const isFocused = focusedNodeId === node.id;
        
        return (
            <TreeNode 
                key={node.uniqueKey}
                onClick={() => {
                    if (node.hasChildren) {
                        handleToggle(node.id, node.level);
                        // Only focus when expanding (not when collapsing)
                        if (!isExpanded) {
                            setFocusedNodeId(node.id);
                        } else if (focusedNodeId === node.id) {
                            // Clear focus if collapsing the currently focused node
                            setFocusedNodeId(null);
                        }
                    }
                }}
                sx={{ 
                    cursor: node.hasChildren ? 'pointer' : 'default' 
                }}>
                {/* Render parent continuation lines */}
                {node.parentLines.map((showLine, idx) => (
                    <TreeLine key={idx}>
                        {showLine ? '│  ' : '   '}
                    </TreeLine>
                ))}
                
                {/* Render the connector */}
                {node.level > 0 && (
                    <TreeLine>
                        {node.isLast ? '└─ ' : '├─ '}
                    </TreeLine>
                )}
                
                {/* Render expand/collapse arrow */}
                {node.hasChildren ? (
                    <TreeArrow>
                        {isExpanded ? '▼' : '▶'}
                    </TreeArrow>
                ) : (
                    <span style={{ display: 'inline-block', width: '16px' }}></span>
                )}
                
                {/* Render node name */}
                <Typography 
                    variant="body2" 
                    component="span" 
                    sx={{ 
                        ml: 0.5,
                        fontWeight: isFocused ? 'bold' : 400
                    }}
                >
                    {node.name}
                    {node.department && (
                        <Typography 
                            component="span" 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ ml: 0.5 }}
                        >
                            ({node.department})
                        </Typography>
                    )}
                </Typography>
            </TreeNode>
        );
    };

    if (treeError || tableError) {
        return (
            <Grid container>
                <Grid item xs={12}>
                    <MainCard sx={{ bgcolor: 'background.default' }}>
                        <Typography color="error">Error loading line managers data</Typography>
                    </MainCard>
                </Grid>
            </Grid>
        );
    }

    return (
        <Grid container rowSpacing={4.5} columnSpacing={2.75}>
            <Grid item xs={12}>
                <MainCard sx={{ bgcolor: 'background.default' }}>

                    {/* Split View Container */}
                    <Box sx={{ display: 'flex', gap: 2, height: 'calc(100vh - 400px)', minHeight: '400px', bgcolor: 'background.default' }}>
                        {/* Left Panel - Hierarchy Tree (30%) */}
                        <TreeContainer sx={{ 
                            flex: '0 0 30%', 
                            minWidth: '250px',
                            maxWidth: '400px',
                            position: 'relative',
                            bgcolor: 'background.default'
                        }}>
                            {treeLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                    <Typography>Loading line managers...</Typography>
                                </Box>
                            ) : visibleNodes.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="h6" color="text.secondary">
                                        No line managers found
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        No users have been assigned as line managers
                                    </Typography>
                                </Box>
                            ) : (
                                visibleNodes.map(node => renderTreeNode(node))
                            )}
                        </TreeContainer>

                        {/* Right Panel - Data Table (70%) */}
                        <Box sx={{ 
                            flex: '1 1 70%', 
                            overflow: 'hidden',
                            minWidth: 0, // This allows the flex item to shrink below its content width
                            display: 'flex',
                            flexDirection: 'column',
                            bgcolor: 'background.default'
                        }}>
                            <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Email</TableCell>
                                            <TableCell>Department</TableCell>
                                            <TableCell>Line Manager</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {tableLoading ? (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center">
                                                    <Typography>Loading users...</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : tableUsers.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center">
                                                    <Typography>No users found</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            tableUsers.map((user) => (
                                                <TableRow
                                                    key={user.id}
                                                    hover
                                                    sx={{ cursor: 'default' }}
                                                >
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {user.email}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>{user.department || '-'}</TableCell>
                                                    <TableCell>
                                                        {user.lineManagerName ? (
                                                            <Typography variant="body2">
                                                                {user.lineManagerName}
                                                                {user.lineManagerEmail && ` (${user.lineManagerEmail})`}
                                                            </Typography>
                                                        ) : '-'}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                count={tableData?.rowsAffected || 0}
                                rowsPerPage={paginationModel.pageSize}
                                page={paginationModel.page}
                                onPageChange={handlePageChange}
                                onRowsPerPageChange={handleRowsPerPageChange}
                                rowsPerPageOptions={[50, 100, 200, 500]}
                            />
                        </Box>
                    </Box>
                </MainCard>
            </Grid>
        </Grid>
    );
};

export default LineManagerDefault;